import { useEffect, useRef, useState } from 'react';

import { X } from 'lucide-react';

import Typography from 'components/Typography/Typography';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  iframeSrc?: string;
  previewData?: any; // Add your specific data type here
}

const randomMessageBlock = () => {
  const BLOCK_TYPES = [
    'text',
    'image',
    'video',
    'audio',
    'link',
    'quote',
    'code'
  ];

  return {
    message: Math.random().toString(36).substring(2, 15),
    blockType: BLOCK_TYPES[Math.floor(Math.random() * BLOCK_TYPES.length)]
  };
};

export const PreviewModal = ({
  isOpen,
  onClose,
  iframeSrc = 'https://localhost:3001/preview/123',
  previewData
}: PreviewModalProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  const sendMessage = () => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'PREVIEW_DATA',
        data: randomMessageBlock()
      },
      '*'
    );
  };

  useEffect(() => {
    if (isOpen && iframeRef.current) {
      const sendMessage = () => {
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: 'PREVIEW_DATA',
            data: previewData
          },
          '*'
        );
      };
      iframeRef.current.onload = sendMessage;
      sendMessage();
    }
  }, [isOpen, previewData]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] h-[90vh] max-w-[90vw] max-h-[90vh] p-0 bg-white">
        <div className="relative h-full">
          <div className="flex flex-col h-full rounded-lg overflow-hidden border border-gray-200">
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="flex-1 mx-4">
                <div className="bg-white w-full px-3 py-1 rounded text-sm text-gray-600 border border-gray-300">
                  Preview
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={sendMessage}
                  className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  {/* Send Message */}
                </button>
                <button
                  onClick={onClose}
                  className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 bg-white p-6">
              <div className="w-full h-full relative">
                {isLoading && (
                  <div className="absolute inset-0 z-10 bg-gray-100">
                    <div className="h-16 mb-6">
                      <Skeleton className="w-full h-full" />
                    </div>
                    <div className="px-6">
                      <div className="mb-8">
                        <Skeleton className="h-8 w-1/3 mb-4" />
                        <Skeleton className="h-4 w-2/3 mb-2" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="space-y-3">
                            <Skeleton className="w-full aspect-video" />
                            <Skeleton className="h-4 w-5/6" />
                            <Skeleton className="h-4 w-4/6" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <iframe
                  onLoad={() => setIsLoading(false)}
                  ref={iframeRef}
                  src={iframeSrc}
                  className="absolute top-0 left-0 w-full h-full border-0"
                  title="Preview Content"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
